import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

import ProgressiveImage from '../../../components/ui/ProgressiveImage';

const VERDICT_CONFIG = {
  fair: { label: 'Fair', color: '#22C55E', bg: 'rgba(34, 197, 94, 0.15)', border: 'rgba(34, 197, 94, 0.3)' },
  risky: { label: 'Risky', color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.15)', border: 'rgba(245, 158, 11, 0.3)' },
  aggressive: { label: 'Aggressive', color: '#EF4444', bg: 'rgba(239, 68, 68, 0.15)', border: 'rgba(239, 68, 68, 0.3)' },
  overpriced: { label: 'Overpriced', color: '#EF4444', bg: 'rgba(239, 68, 68, 0.15)', border: 'rgba(239, 68, 68, 0.3)' },
  underpriced: { label: 'Underpriced', color: '#22C55E', bg: 'rgba(34, 197, 94, 0.15)', border: 'rgba(34, 197, 94, 0.3)' },
};

const formatPrice = (price) => {
  if (!price) return 'Contact for price';
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 })?.format(price);
};

const getBestValue = (properties, key) => {
  const values = properties?.map(p => {
    if (key === 'price') return p?.price_or_rent || Infinity;
    if (key === 'beds') return p?.properties?.beds || 0;
    if (key === 'baths') return p?.properties?.baths || 0;
    if (key === 'parking') return p?.properties?.parking || 0;
    return null;
  });
  if (key === 'price') return Math.min(...values?.filter(v => v !== Infinity));
  return Math.max(...values);
};

const ComparisonTable = ({ properties, onRemove }) => {
  const navigate = useNavigate();
  const [expandedRows, setExpandedRows] = useState({});

  const toggleRow = (rowKey) => {
    setExpandedRows(prev => ({ ...prev, [rowKey]: !prev?.[rowKey] }));
  };

  const bestPrice = getBestValue(properties, 'price');
  const bestBeds = getBestValue(properties, 'beds');
  const bestBaths = getBestValue(properties, 'baths');
  const bestParking = getBestValue(properties, 'parking');

  const isBest = (prop, key) => {
    if (key === 'price') return (prop?.price_or_rent || Infinity) === bestPrice;
    if (key === 'beds') return (prop?.properties?.beds || 0) === bestBeds && bestBeds > 0;
    if (key === 'baths') return (prop?.properties?.baths || 0) === bestBaths && bestBaths > 0;
    if (key === 'parking') return (prop?.properties?.parking || 0) === bestParking && bestParking > 0;
    return false;
  };

  const getBestVerdictIndex = () => {
    const order = ['fair', 'underpriced', 'risky', 'aggressive', 'overpriced'];
    let bestIdx = -1;
    let bestScore = Infinity;
    properties?.forEach((p, i) => {
      const verdict = p?.aiAnalysis?.verdict?.toLowerCase() || '';
      const score = order?.indexOf(verdict);
      if (score !== -1 && score < bestScore) { bestScore = score; bestIdx = i; }
    });
    return bestIdx;
  };
  const bestVerdictIdx = getBestVerdictIndex();

  const rows = [
    { key: 'price', label: 'Asking Price', icon: 'DollarSign' },
    { key: 'beds', label: 'Bedrooms', icon: 'Bed' },
    { key: 'baths', label: 'Bathrooms', icon: 'Bath' },
    { key: 'parking', label: 'Parking', icon: 'Car' },
    { key: 'type', label: 'Property Type', icon: 'Home' },
    { key: 'suburb', label: 'Location', icon: 'MapPin' },
    { key: 'landSize', label: 'Land Size', icon: 'Maximize2' },
    { key: 'verdict', label: 'AI Verdict', icon: 'Brain', expandable: true },
    { key: 'offerBand', label: 'Offer Band', icon: 'TrendingUp' },
    { key: 'strengths', label: 'Key Strengths', icon: 'ThumbsUp', expandable: true },
    { key: 'risks', label: 'Key Risks', icon: 'AlertTriangle', expandable: true },
  ];

  const getCellValue = (prop, key) => {
    const p = prop?.properties;
    const ai = prop?.aiAnalysis;
    switch (key) {
      case 'price': return formatPrice(prop?.price_or_rent);
      case 'beds': return p?.beds ? `${p?.beds} bed${p?.beds > 1 ? 's' : ''}` : '—';
      case 'baths': return p?.baths ? `${p?.baths} bath${p?.baths > 1 ? 's' : ''}` : '—';
      case 'parking': return p?.parking ? `${p?.parking} space${p?.parking > 1 ? 's' : ''}` : '—';
      case 'type': return p?.type ? p?.type?.charAt(0)?.toUpperCase() + p?.type?.slice(1) : '—';
      case 'suburb': return p?.suburb ? `${p?.suburb}${p?.state ? ', ' + p?.state : ''}` : '—';
      case 'landSize': return p?.land_size ? `${p?.land_size} m²` : '—';
      case 'verdict': return ai?.verdict || 'Not Analysed';
      case 'offerBand': return ai?.offerBand ? `${formatPrice(ai?.offerBand?.min)} – ${formatPrice(ai?.offerBand?.max)}` : 'Not Analysed';
      case 'strengths': return ai?.strengths || [];
      case 'risks': return ai?.risks || [];
      default: return '—';
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-max border-collapse">
        {/* Property Headers */}
        <thead>
          <tr>
            <th
              className="text-left px-4 py-3 text-sm font-semibold w-40 sticky left-0 z-20"
              style={{ background: '#0A1628', color: '#6B9EC4', borderBottom: '1px solid rgba(0, 212, 255, 0.15)' }}
            >
              Feature
            </th>
            {properties?.map((prop, idx) => (
              <th
                key={prop?.id || idx}
                className="px-4 py-3 min-w-56"
                style={{ borderBottom: '1px solid rgba(0, 212, 255, 0.15)', background: '#0D1B2A' }}
              >
                <div className="relative">
                  {/* Remove button */}
                  <button
                    onClick={() => onRemove(prop?.id)}
                    className="absolute -top-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center z-10 transition-all"
                    style={{ background: 'rgba(255, 107, 107, 0.15)', color: '#FF6B6B', border: '1px solid rgba(255, 107, 107, 0.3)' }}
                    title="Remove from comparison"
                  >
                    <Icon name="X" size={12} color="currentColor" />
                  </button>

                  {/* Property image */}
                  <div className="w-full h-32 rounded-lg overflow-hidden mb-3">
                    <ProgressiveImage
                      src={prop?.media?.[0]?.enhanced_url || prop?.media?.[0]?.original_url || '/assets/images/no_image.png'}
                      alt={`Property at ${prop?.properties?.address}`}
                      className="w-full h-full"
                    />
                  </div>

                  {/* Best value badge */}
                  {idx === bestVerdictIdx && (
                    <div
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold mb-2"
                      style={{ background: 'rgba(34, 197, 94, 0.15)', color: '#22C55E', border: '1px solid rgba(34, 197, 94, 0.3)' }}
                    >
                      <Icon name="Star" size={10} color="currentColor" />
                      Best Value
                    </div>
                  )}

                  <p className="text-sm font-semibold text-white text-left leading-tight">
                    {prop?.properties?.address}
                  </p>
                  <p className="text-xs mt-0.5 text-left" style={{ color: '#6B9EC4' }}>
                    {prop?.properties?.suburb}{prop?.properties?.state ? ', ' + prop?.properties?.state : ''}
                  </p>

                  <button
                    onClick={() => navigate(`/listing/${prop?.id}`)}
                    className="mt-2 w-full py-1.5 rounded-lg text-xs font-semibold transition-all text-left px-3"
                    style={{ background: 'rgba(0, 212, 255, 0.1)', color: '#00D4FF', border: '1px solid rgba(0, 212, 255, 0.2)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(0, 212, 255, 0.2)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(0, 212, 255, 0.1)'}
                  >
                    View Full Analysis →
                  </button>
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows?.map((row, rowIdx) => (
            <tr
              key={row?.key}
              style={{ background: rowIdx % 2 === 0 ? '#0A1628' : '#0D1B2A' }}
            >
              {/* Row label */}
              <td
                className="px-4 py-3 sticky left-0 z-10"
                style={{
                  background: rowIdx % 2 === 0 ? '#0A1628' : '#0D1B2A',
                  borderBottom: '1px solid rgba(0, 212, 255, 0.06)'
                }}
              >
                <div className="flex items-center gap-2">
                  <Icon name={row?.icon} size={14} color="#6B9EC4" />
                  <span className="text-sm font-medium whitespace-nowrap" style={{ color: '#8BA5C0' }}>
                    {row?.label}
                  </span>
                  {row?.expandable && (
                    <button
                      onClick={() => toggleRow(row?.key)}
                      className="ml-1 transition-transform"
                      style={{ color: '#6B9EC4', transform: expandedRows?.[row?.key] ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    >
                      <Icon name="ChevronDown" size={12} color="currentColor" />
                    </button>
                  )}
                </div>
              </td>

              {/* Property values */}
              {properties?.map((prop, colIdx) => {
                const value = getCellValue(prop, row?.key);
                const best = isBest(prop, row?.key);
                const verdictKey = row?.key === 'verdict' ? value?.toLowerCase() : null;
                const verdictCfg = verdictKey ? VERDICT_CONFIG?.[verdictKey] : null;
                const isArray = Array.isArray(value);

                return (
                  <td
                    key={colIdx}
                    className="px-4 py-3 align-top"
                    style={{
                      borderBottom: '1px solid rgba(0, 212, 255, 0.06)',
                      background: best ? 'rgba(34, 197, 94, 0.05)' : 'transparent',
                    }}
                  >
                    {verdictCfg ? (
                      <div>
                        <span
                          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold"
                          style={{ background: verdictCfg?.bg, color: verdictCfg?.color, border: `1px solid ${verdictCfg?.border}` }}
                        >
                          <Icon name="Shield" size={12} color="currentColor" />
                          {verdictCfg?.label}
                        </span>
                        {expandedRows?.[row?.key] && prop?.aiAnalysis?.confidence && (
                          <p className="text-xs mt-1" style={{ color: '#6B9EC4' }}>
                            Confidence: {prop?.aiAnalysis?.confidence}%
                          </p>
                        )}
                      </div>
                    ) : isArray ? (
                      <div>
                        {value?.length === 0 ? (
                          <span className="text-sm" style={{ color: '#4A6580' }}>Not Analysed</span>
                        ) : (
                          <>
                            <ul className="space-y-1">
                              {(expandedRows?.[row?.key] ? value : value?.slice(0, 2))?.map((item, i) => (
                                <li key={i} className="flex items-start gap-1.5">
                                  <span className="mt-1 flex-shrink-0">
                                    <Icon
                                      name={row?.key === 'strengths' ? 'CheckCircle' : 'AlertCircle'}
                                      size={12}
                                      color={row?.key === 'strengths' ? '#22C55E' : '#F59E0B'}
                                    />
                                  </span>
                                  <span className="text-xs leading-relaxed" style={{ color: '#8BA5C0' }}>{item}</span>
                                </li>
                              ))}
                            </ul>
                            {value?.length > 2 && (
                              <button
                                onClick={() => toggleRow(row?.key)}
                                className="text-xs mt-1 font-medium"
                                style={{ color: '#00D4FF' }}
                              >
                                {expandedRows?.[row?.key] ? 'Show less' : `+${value?.length - 2} more`}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span
                          className="text-sm font-medium"
                          style={{ color: best ? '#22C55E' : '#E2E8F0' }}
                        >
                          {value}
                        </span>
                        {best && (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-semibold"
                            style={{ background: 'rgba(34, 197, 94, 0.15)', color: '#22C55E' }}
                          >
                            <Icon name="TrendingUp" size={10} color="currentColor" />
                            Best
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ComparisonTable;
